import { useState, useEffect } from "react";

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(201,168,76,0.25)", goldBg:"rgba(201,168,76,0.08)",
  gold:"#c9a84c", goldL:"#e8c96a", goldD:"#8a6f2e",
  green:"#2ecc87", greenL:"rgba(46,204,135,0.12)",
  red:"#e05555", redL:"rgba(224,85,85,0.12)",
  blue:"#4a9eff", blueL:"rgba(74,158,255,0.12)",
  purple:"#9b6ef3", purpleL:"rgba(155,110,243,0.12)",
  teal:"#2dd4bf", tealL:"rgba(45,212,191,0.12)",
  orange:"#f97316", orangeL:"rgba(249,115,22,0.12)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F="'Montserrat','Plus Jakarta Sans',sans-serif";
const S="'Playfair Display','Cormorant Garamond',Georgia,serif";

// ── CREDIT SCORE LOGIC ────────────────────────────────────────────────────────
const scoreInfo=(s)=>{
  if(s>=750)return{label:"Excellent",c:C.green,bg:C.greenL,max:10000,apr:9.99};
  if(s>=700)return{label:"Good",c:C.teal,bg:C.tealL,max:7500,apr:14.99};
  if(s>=650)return{label:"Fair",c:C.gold,bg:C.goldBg,max:4000,apr:19.99};
  if(s>=600)return{label:"Poor",c:C.orange,bg:C.orangeL,max:2000,apr:24.99};
  return{label:"Declined",c:C.red,bg:C.redL,max:0,apr:0};
};

const calcPlan=(amount,months,apr)=>{
  const r=apr/100/12;
  if(r===0)return{monthly:amount/months,total:amount,interest:0};
  const m=(amount*r*Math.pow(1+r,months))/(Math.pow(1+r,months)-1);
  return{monthly:Math.round(m*100)/100,total:Math.round(m*months*100)/100,interest:Math.round((m*months-amount)*100)/100};
};

// ── DATA ──────────────────────────────────────────────────────────────────────
const INIT_PLANS=[
  {id:1,client:"Dana Whitfield",cid:3,amount:2800,approved:2800,score:762,apr:9.99,months:12,monthly:245.18,balance:1960,paid:840,status:"active",startDate:"2024-01-01",nextDue:"2024-02-01",treats:["Sculptra","Cheek Filler"],signed:true},
  {id:2,client:"Rachel Summers",cid:1,amount:1800,approved:1800,score:718,apr:14.99,months:6,monthly:320.44,balance:640,paid:1160,status:"active",startDate:"2023-11-01",nextDue:"2024-02-01",treats:["Ultherapy"],signed:true},
  {id:3,client:"Noah Bennett",cid:5,amount:1200,approved:1200,score:694,apr:19.99,months:6,monthly:218.32,balance:0,paid:1200,status:"completed",startDate:"2023-08-01",nextDue:null,treats:["RF Microneedling","Botox"],signed:true},
];

const GIFT_CARDS=[
  {id:1,code:"ELYON-4829-XKZQ",recipient:"Sarah Johnson",from:"Dana Whitfield",amount:500,balance:320,issued:"2024-01-10",expiry:"2025-01-10",status:"active"},
  {id:2,code:"ELYON-7734-MLPW",recipient:"Jennifer Torres",from:"Self",amount:250,balance:250,issued:"2024-01-15",expiry:"2025-01-15",status:"active"},
  {id:3,code:"ELYON-2291-RVNB",recipient:"Ashley Kim",from:"Noah Bennett",amount:300,balance:0,issued:"2023-12-01",expiry:"2024-12-01",status:"redeemed"},
];

const LOYALTY_TIERS=[
  {name:"Bronze",min:0,max:999,color:"#cd7f32",perks:["1 pt per $1 spent","Birthday bonus 50 pts"]},
  {name:"Silver",min:1000,max:2499,color:"#9090a8",perks:["1.25 pts per $1 spent","Birthday bonus 100 pts","Free consultation/yr"]},
  {name:"Gold",min:2500,max:4999,color:C.gold,perks:["1.5 pts per $1 spent","Birthday bonus 200 pts","2 free consultations/yr","Priority booking"]},
  {name:"Platinum",min:5000,max:999999,color:"#e8e8f0",perks:["2 pts per $1 spent","Birthday bonus 500 pts","Unlimited consultations","VIP events","Dedicated coordinator"]},
];

const CLIENTS_LOYALTY=[
  {id:1,name:"Dana Whitfield",pts:1260,tier:"Gold",ytdSpend:12600,redeemed:500,available:760},
  {id:2,name:"Claire Fontaine",pts:670,tier:"Silver",ytdSpend:6700,redeemed:200,available:470},
  {id:3,name:"Rachel Summers",pts:840,tier:"Gold",ytdSpend:8400,redeemed:300,available:540},
  {id:4,name:"Noah Bennett",pts:420,tier:"Bronze",ytdSpend:4200,redeemed:100,available:320},
];

const RETAIL_PRODUCTS=[
  {id:1,name:"SkinCeuticals C E Ferulic",cat:"Vitamin C",price:182,stock:12,img:"🧴",brand:"SkinCeuticals",desc:"Award-winning antioxidant serum"},
  {id:2,name:"Alastin Regenerating Skin Nectar",cat:"Post-Treatment",price:195,stock:8,img:"✨",brand:"Alastin",desc:"Post-procedure recovery"},
  {id:3,name:"EltaMD UV Clear SPF 46",cat:"Sunscreen",price:42,stock:24,img:"☀️",brand:"EltaMD",desc:"Oil-free broad-spectrum SPF"},
  {id:4,name:"ZO Skin Health Exfoliating Polish",cat:"Exfoliants",price:65,stock:15,img:"💎",brand:"ZO Skin Health",desc:"Daily renewal texture"},
  {id:5,name:"iS Clinical Active Serum",cat:"Serums",price:150,stock:6,img:"💧",brand:"iS Clinical",desc:"Multi-action corrective serum"},
  {id:6,name:"Revision Nectifirm Advanced",cat:"Neck & Décolleté",price:120,stock:10,img:"🌿",brand:"Revision",desc:"Firms and tightens neck area"},
];

const PORTAL_CLIENT={
  id:1,name:"Rachel Summers",email:"rachel@email.com",phone:"(310) 555-0142",
  appointments:[
    {id:1,treat:"Botox",prov:"Dr. Ashley Monroe",date:"2024-01-24",time:"10:00",status:"confirmed",price:800},
    {id:4,treat:"Botox",prov:"Dr. Ashley Monroe",date:"2024-01-22",time:"14:00",status:"completed",price:700},
  ],
  loyaltyPts:840,tier:"Gold",memberPlan:"Gold Glow",
  paymentPlan:{amount:1800,balance:640,monthly:320.44,nextDue:"2024-02-01"},
  forms:[{name:"Botox Consent",status:"signed",date:"Jan 22"},{name:"HIPAA Auth",status:"signed",date:"Nov 10"}],
  beautyBank:540,
};

// ── SHARED UI ─────────────────────────────────────────────────────────────────
const inp={width:"100%",padding:"10px 14px",background:"#f3f4f6",border:"1px solid #e8e4df",borderRadius:8,fontSize:13,color:"#1a1a2e",fontFamily:F,outline:"none",boxSizing:"border-box"};
const Fld=({label,children,col})=>(<div style={{gridColumn:col}}><label style={{display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:7}}>{label}</label>{children}</div>);

function Btn({children,v="primary",onClick,sz="md",icon,full,style:ex}){
  const sizes={sm:{padding:"5px 12px",fontSize:11},md:{padding:"9px 18px",fontSize:13},lg:{padding:"12px 28px",fontSize:14}};
  const vars={
    primary:{background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#ffffff",boxShadow:`0 2px 12px rgba(201,168,76,0.25)`},
    ghost:{background:"transparent",color:C.muted,border:`1px solid ${C.border}`},
    danger:{background:C.redL,color:C.red,border:"1px solid rgba(224,85,85,0.25)"},
    success:{background:C.greenL,color:C.green,border:"1px solid rgba(46,204,135,0.25)"},
    sec:{background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`},
    dark:{background:C.light,color:C.text,border:`1px solid ${C.border}`},
  };
  return <button onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:6,borderRadius:8,fontWeight:600,cursor:"pointer",fontFamily:F,border:"none",transition:"all 0.15s",whiteSpace:"nowrap",justifyContent:"center",...sizes[sz],...(vars[v]||vars.ghost),...(full?{width:"100%"}:{}),...ex}}>{children}</button>;
}

function Card({children,style,onClick}){
  return <div onClick={onClick} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",...style}}>{children}</div>;
}

function Modal({title,onClose,children,w=580}){
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(8px)"}}>
      <div style={{background:C.card,border:`1px solid ${C.borderGold}`,borderRadius:16,width:"100%",maxWidth:w,maxHeight:"90vh",overflow:"auto",boxShadow:"0 32px 80px rgba(0,0,0,0.6)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 24px",borderBottom:`1px solid ${C.border}`}}>
          <h3 style={{margin:0,fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,letterSpacing:"0.05em"}}>{title}</h3>
          <button onClick={onClose} style={{background:C.light,border:"none",borderRadius:8,padding:6,cursor:"pointer",color:C.muted,display:"flex"}}><span style={{fontSize:16,lineHeight:1}}>✕</span></button>
        </div>
        <div style={{padding:24}}>{children}</div>
      </div>
    </div>
  );
}

function StatCard({label,value,sub,color,trend}){
  return(
    <Card style={{padding:"20px 22px"}}>
      <div style={{fontSize:26,fontWeight:800,color,fontFamily:S,marginBottom:4}}>{value}</div>
      <div style={{fontSize:12,color:C.muted,fontWeight:500}}>{label}</div>
      {sub&&<div style={{fontSize:11,color:C.dim,marginTop:2}}>{sub}</div>}
      {trend!==undefined&&<div style={{fontSize:11,fontWeight:700,color:trend>0?C.green:C.red,marginTop:4}}>{trend>0?"+":""}{trend}% vs last month</div>}
    </Card>
  );
}

// ── ELYON PAY ─────────────────────────────────────────────────────────────────
function ElyonPay(){
  const [view,setView]=useState("dashboard");
  const [checkStep,setCheckStep]=useState(1);
  const [checking,setChecking]=useState(false);
  const [checkResult,setCheckResult]=useState(null);
  const [form,setForm]=useState({name:"",ssn:"",dob:"",address:"",income:"",amount:"",client:""});
  const [selPlan,setSelPlan]=useState(null);
  const [signed,setSigned]=useState(false);
  const [sigName,setSigName]=useState("");
  const [plans,setPlans]=useState(INIT_PLANS);
  const [spaSettings,setSpaSettings]=useState({maxLimit:10000,defaultAPR:14.99,plans:[3,6,12,24],requireCredit:true,downPaymentPct:10});

  const totalPortfolio=plans.filter(p=>p.status==="active").reduce((s,p)=>s+p.balance,0);
  const totalInterestEarned=plans.reduce((s,p)=>s+(p.paid*(p.apr/100/12)),0);
  const monthlyRecurring=plans.filter(p=>p.status==="active").reduce((s,p)=>s+p.monthly,0);

  const runCreditCheck=()=>{
    setChecking(true);
    setTimeout(()=>{
      const score=Math.floor(Math.random()*(800-580)+580);
      const info=scoreInfo(score);
      const income=parseInt(form.income)||5000;
      const approvedAmount=Math.min(info.max,Math.round(income*0.25/100)*100,spaSettings.maxLimit);
      setCheckResult({score,info,approvedAmount,income});
      setChecking(false);
      setCheckStep(3);
    },3000);
  };

  const resetCheck=()=>{setCheckStep(1);setCheckResult(null);setSelPlan(null);setSigned(false);setSigName("");setForm({name:"",ssn:"",dob:"",address:"",income:"",amount:"",client:""});};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {/* Tabs */}
      <div style={{display:"flex",gap:4,background:C.surface,padding:4,borderRadius:10,border:`1px solid ${C.border}`,width:"fit-content"}}>
        {[["dashboard","💳 Dashboard"],["new","✨ New Application"],["plans","📋 Active Plans"],["settings","⚙️ Settings"]].map(([v,l])=>(
          <button key={v} onClick={()=>{setView(v);if(v==="new")resetCheck();}} style={{padding:"7px 16px",fontSize:12,fontWeight:view===v?700:500,color:view===v?"#09090b":C.muted,background:view===v?C.gold:"none",borderRadius:7,border:"none",cursor:"pointer",fontFamily:F,transition:"all 0.15s"}}>{l}</button>
        ))}
      </div>

      {/* Dashboard */}
      {view==="dashboard"&&(
        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14}}>
            <StatCard label="Active Portfolio" value={`$${totalPortfolio.toLocaleString()}`} color={C.gold} sub="Outstanding balance" trend={22}/>
            <StatCard label="Monthly Recurring" value={`$${Math.round(monthlyRecurring).toLocaleString()}`} color={C.green} sub="Expected payments"/>
            <StatCard label="Active Plans" value={plans.filter(p=>p.status==="active").length} color={C.blue} sub={`${plans.length} total plans`}/>
            <StatCard label="Finance Charges Earned" value={`$${Math.round(totalInterestEarned*12).toLocaleString()}`} color={C.purple} sub="Projected annual" trend={15}/>
          </div>

          {/* How it works */}
          <Card style={{padding:22,background:`linear-gradient(135deg,rgba(201,168,76,0.06),rgba(201,168,76,0.02))`}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4}}>💳 Elyon Pay — In-House Financing</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Your spa acts as the lender. Earn finance charges. No third-party fees. Full control.</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
              {[["1","Soft Credit Check","No impact on client score"],["2","Set Terms","APR, months, down payment"],["3","Digital Contract","e-Signature protection"],["4","Auto Payments","Charged to card on file"],["5","You Earn","Finance charges go to you"]].map(([n,t,s])=>(
                <div key={n} style={{textAlign:"center",padding:"14px 10px",background:C.surface,borderRadius:10,border:`1px solid ${C.border}`}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:C.goldBg,border:`1px solid ${C.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:C.gold,margin:"0 auto 8px"}}>{n}</div>
                  <div style={{fontSize:11,fontWeight:700,color:C.text,marginBottom:3}}>{t}</div>
                  <div style={{fontSize:10,color:C.muted}}>{s}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Plans */}
          <Card>
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Payment Plans</div>
              <Btn sz="sm" v="sec" onClick={()=>{setView("new");resetCheck();}}>+ New Plan</Btn>
            </div>
            {plans.map(p=>{
              const pct=Math.round((p.paid/p.amount)*100);
              return(
                <div key={p.id} style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:C.text}}>{p.client}</div>
                      <div style={{fontSize:12,color:C.muted}}>{p.treats.join(", ")} · {p.months} months · {p.apr}% APR</div>
                      {p.nextDue&&<div style={{fontSize:11,color:p.status==="active"?C.gold:C.muted}}>Next: ${p.monthly}/mo · Due {p.nextDue}</div>}
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S}}>${p.amount.toLocaleString()}</div>
                      <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:p.status==="active"?C.greenL:C.blueL,color:p.status==="active"?C.green:C.blue,fontWeight:600}}>{p.status}</span>
                    </div>
                  </div>
                  <div style={{marginBottom:6}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.muted,marginBottom:4}}>
                      <span>Paid: ${p.paid.toLocaleString()}</span>
                      <span>Remaining: ${p.balance.toLocaleString()}</span>
                      <span>{pct}%</span>
                    </div>
                    <div style={{height:5,background:C.light,borderRadius:3}}>
                      <div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${C.goldD},${C.gold})`,borderRadius:3,transition:"width 0.5s"}}/>
                    </div>
                  </div>
                  {p.signed&&<span style={{fontSize:11,color:C.green,background:C.greenL,padding:"2px 8px",borderRadius:8,fontWeight:600}}>✓ Contract Signed</span>}
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* New Application */}
      {view==="new"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 380px",gap:20}}>
          <div>
            {/* Progress */}
            <div style={{display:"flex",gap:0,marginBottom:24,background:C.surface,borderRadius:12,padding:4,border:`1px solid ${C.border}`}}>
              {[["1","Client Info"],["2","Soft Pull"],["3","Results"],["4","Select Plan"],["5","Sign Contract"]].map(([n,l],i)=>(
                <div key={n} style={{flex:1,textAlign:"center",padding:"8px 4px",borderRadius:8,background:checkStep>i+1?C.greenL:checkStep===i+1?C.goldBg:"transparent",transition:"all 0.3s"}}>
                  <div style={{fontSize:11,fontWeight:700,color:checkStep>i+1?C.green:checkStep===i+1?C.gold:C.dim}}>{checkStep>i+1?"✓":n}</div>
                  <div style={{fontSize:10,color:checkStep===i+1?C.gold:C.dim,marginTop:2}}>{l}</div>
                </div>
              ))}
            </div>

            {/* Step 1 — Client Info */}
            {checkStep===1&&(
              <Card style={{padding:24}}>
                <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4}}>Step 1 — Client Information</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:20}}>This information is used for the soft credit check only. No hard pull — will not affect credit score.</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                  <Fld label="Full Legal Name" col="1/-1"><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="As it appears on ID" style={inp}/></Fld>
                  <Fld label="Last 4 of SSN"><input value={form.ssn} onChange={e=>setForm({...form,ssn:e.target.value.slice(0,4)})} placeholder="****" maxLength={4} style={inp} type="password"/></Fld>
                  <Fld label="Date of Birth"><input type="date" value={form.dob} onChange={e=>setForm({...form,dob:e.target.value})} style={inp}/></Fld>
                  <Fld label="Home Address" col="1/-1"><input value={form.address} onChange={e=>setForm({...form,address:e.target.value})} placeholder="123 Main St, City, State, ZIP" style={inp}/></Fld>
                  <Fld label="Monthly Income ($)"><input type="number" value={form.income} onChange={e=>setForm({...form,income:e.target.value})} placeholder="Self-reported" style={inp}/></Fld>
                  <Fld label="Requested Amount ($)"><input type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="Treatment cost" style={inp}/></Fld>
                </div>
                <div style={{marginTop:16,padding:"12px 14px",background:C.goldBg,borderRadius:9,border:`1px solid ${C.borderGold}`,fontSize:12,color:C.gold}}>
                  🔒 Soft pull only — This will NOT appear on the client's credit report and will NOT affect their credit score. Powered by Experian.
                </div>
                <div style={{marginTop:16,display:"flex",justifyContent:"flex-end"}}>
                  <Btn onClick={()=>setCheckStep(2)} disabled={!form.name||!form.ssn||!form.income}>Continue to Credit Check →</Btn>
                </div>
              </Card>
            )}

            {/* Step 2 — Running Check */}
            {checkStep===2&&(
              <Card style={{padding:40,textAlign:"center"}}>
                <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:8}}>Step 2 — Soft Credit Pull</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:32}}>Securely checking credit profile via Experian...</div>
                {!checking?(
                  <div>
                    <div style={{padding:"20px",background:C.goldBg,borderRadius:12,border:`1px solid ${C.borderGold}`,marginBottom:24,textAlign:"left"}}>
                      <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:8}}>{form.name}</div>
                      {[["SSN (last 4)","****"+form.ssn],["Date of Birth",form.dob],["Monthly Income",`$${parseInt(form.income||0).toLocaleString()}`],["Requested",`$${parseInt(form.amount||0).toLocaleString()}`]].map(([k,v])=>(
                        <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"5px 0",borderBottom:`1px solid ${C.border}`}}>
                          <span style={{color:C.muted}}>{k}</span><span style={{color:C.text}}>{v}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{fontSize:11,color:C.muted,marginBottom:20}}>By proceeding, the client consents to a soft credit inquiry that will not affect their credit score.</div>
                    <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                      <Btn v="ghost" onClick={()=>setCheckStep(1)}>← Back</Btn>
                      <Btn onClick={runCreditCheck}>🔍 Run Soft Pull</Btn>
                    </div>
                  </div>
                ):(
                  <div>
                    <div style={{width:80,height:80,borderRadius:"50%",border:`3px solid ${C.border}`,borderTop:`3px solid ${C.gold}`,margin:"0 auto 24px",animation:"spin 1s linear infinite"}}/>
                    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                    <div style={{display:"flex",flexDirection:"column",gap:10,textAlign:"left",maxWidth:320,margin:"0 auto"}}>
                      {["Connecting to Experian...","Verifying identity...","Analyzing credit profile...","Calculating approval amount..."].map((t,i)=>(
                        <div key={t} style={{display:"flex",gap:10,alignItems:"center",fontSize:12,color:C.muted}}>
                          <div style={{width:16,height:16,borderRadius:"50%",background:C.goldBg,border:`1px solid ${C.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:C.gold,flexShrink:0}}>✓</div>
                          {t}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Step 3 — Results */}
            {checkStep===3&&checkResult&&(
              <Card style={{padding:24}}>
                <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4}}>Step 3 — Credit Results</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:20}}>Soft pull complete — no impact to credit score</div>

                {/* Score Display */}
                <div style={{padding:24,background:C.goldBg,borderRadius:12,border:`1px solid ${C.borderGold}`,marginBottom:20,textAlign:"center"}}>
                  <div style={{fontSize:12,color:C.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.1em"}}>Credit Score</div>
                  <div style={{fontSize:72,fontWeight:800,color:checkResult.info.c,fontFamily:S,lineHeight:1,marginBottom:6}}>{checkResult.score}</div>
                  <div style={{display:"inline-block",padding:"4px 16px",background:checkResult.info.bg,color:checkResult.info.c,borderRadius:20,fontSize:13,fontWeight:700,marginBottom:16}}>{checkResult.info.label}</div>

                  {/* Score Bar */}
                  <div style={{position:"relative",height:8,background:C.light,borderRadius:4,marginBottom:8}}>
                    <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${((checkResult.score-300)/(850-300))*100}%`,background:`linear-gradient(90deg,${C.red},${C.gold},${C.green})`,borderRadius:4}}/>
                    <div style={{position:"absolute",top:-3,width:14,height:14,borderRadius:"50%",background:"#fff",border:`2px solid ${checkResult.info.c}`,left:`calc(${((checkResult.score-300)/(850-300))*100}% - 7px)`,transition:"left 0.5s"}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.dim}}>
                    <span>300</span><span>Poor</span><span>Fair</span><span>Good</span><span>Excellent</span><span>850</span>
                  </div>
                </div>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
                  <div style={{padding:"16px",background:C.surface,borderRadius:10,border:`1px solid ${C.border}`,textAlign:"center"}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4}}>APPROVED AMOUNT</div>
                    <div style={{fontSize:28,fontWeight:800,color:C.green,fontFamily:S}}>${checkResult.approvedAmount.toLocaleString()}</div>
                  </div>
                  <div style={{padding:"16px",background:C.surface,borderRadius:10,border:`1px solid ${C.border}`,textAlign:"center"}}>
                    <div style={{fontSize:11,color:C.muted,marginBottom:4}}>STARTING APR</div>
                    <div style={{fontSize:28,fontWeight:800,color:C.gold,fontFamily:S}}>{checkResult.info.apr}%</div>
                  </div>
                </div>

                {checkResult.approvedAmount>0?(
                  <Btn full onClick={()=>setCheckStep(4)}>View Payment Plans →</Btn>
                ):(
                  <div style={{padding:"16px",background:C.redL,borderRadius:10,border:"1px solid rgba(224,85,85,0.25)",textAlign:"center",color:C.red,fontWeight:600}}>
                    ❌ Unable to approve financing at this time
                  </div>
                )}
              </Card>
            )}

            {/* Step 4 — Select Plan */}
            {checkStep===4&&checkResult&&(
              <Card style={{padding:24}}>
                <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4}}>Step 4 — Select Payment Plan</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:20}}>Approved for ${checkResult.approvedAmount.toLocaleString()} at {checkResult.info.apr}% APR</div>

                <div style={{marginBottom:16}}>
                  <Fld label="Finance Amount ($)">
                    <input type="number" max={checkResult.approvedAmount} defaultValue={Math.min(parseInt(form.amount)||1000,checkResult.approvedAmount)} onChange={e=>setForm({...form,finalAmount:e.target.value})} style={inp}/>
                  </Fld>
                </div>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
                  {spaSettings.plans.map(months=>{
                    const amount=parseInt(form.finalAmount||form.amount||1000);
                    const plan=calcPlan(amount,months,checkResult.info.apr);
                    const isSelected=selPlan?.months===months;
                    return(
                      <div key={months} onClick={()=>setSelPlan({months,amount,...plan,apr:checkResult.info.apr})}
                        style={{padding:"18px",background:isSelected?C.goldBg:C.surface,border:`1.5px solid ${isSelected?C.gold:C.border}`,borderRadius:12,cursor:"pointer",transition:"all 0.2s"}}>
                        <div style={{fontSize:14,fontWeight:800,color:isSelected?C.gold:C.text,fontFamily:S,marginBottom:8}}>{months} Months</div>
                        <div style={{fontSize:24,fontWeight:800,color:isSelected?C.gold:C.text,fontFamily:S,marginBottom:4}}>${plan.monthly.toFixed(2)}<span style={{fontSize:12,fontWeight:400,color:C.muted}}>/mo</span></div>
                        <div style={{fontSize:11,color:C.muted}}>Total: ${plan.total.toFixed(2)}</div>
                        <div style={{fontSize:11,color:C.green,marginTop:2}}>Finance charge: ${plan.interest.toFixed(2)}</div>
                        {isSelected&&<div style={{marginTop:10,fontSize:11,color:C.gold,fontWeight:600}}>✓ Selected</div>}
                      </div>
                    );
                  })}
                </div>

                <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                  <Btn v="ghost" onClick={()=>setCheckStep(3)}>← Back</Btn>
                  <Btn onClick={()=>selPlan&&setCheckStep(5)} disabled={!selPlan}>Continue to Contract →</Btn>
                </div>
              </Card>
            )}

            {/* Step 5 — Sign Contract */}
            {checkStep===5&&selPlan&&(
              <Card style={{padding:24}}>
                <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4}}>Step 5 — Digital Contract & Signature</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:20}}>Review the financing agreement and sign below</div>

                {/* Contract */}
                <div style={{padding:20,background:C.surface,borderRadius:10,border:`1px solid ${C.border}`,marginBottom:20,maxHeight:280,overflow:"auto"}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S,textAlign:"center",marginBottom:4}}>ELYON PAY FINANCING AGREEMENT</div>
                  <div style={{fontSize:11,color:C.dim,textAlign:"center",marginBottom:16}}>Elyon Med Spa Platform · Beverly Hills, CA</div>
                  <div style={{fontSize:12,color:C.text,lineHeight:1.8}}>
                    <p>This Financing Agreement ("Agreement") is entered into between <strong style={{color:C.gold}}>{form.name}</strong> ("Borrower") and the Spa ("Lender") as of {new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}.</p>
                    <p><strong style={{color:C.gold}}>FINANCING TERMS:</strong></p>
                    <p>• Principal Amount: <strong>${selPlan.amount.toLocaleString()}</strong></p>
                    <p>• Annual Percentage Rate (APR): <strong>{selPlan.apr}%</strong></p>
                    <p>• Payment Term: <strong>{selPlan.months} months</strong></p>
                    <p>• Monthly Payment: <strong>${selPlan.monthly.toFixed(2)}</strong></p>
                    <p>• Total Amount Payable: <strong>${selPlan.total.toFixed(2)}</strong></p>
                    <p>• Total Finance Charge: <strong>${selPlan.interest.toFixed(2)}</strong></p>
                    <p><strong style={{color:C.gold}}>PAYMENT TERMS:</strong> Borrower authorizes automatic monthly charges to the card on file. Payments are due on the 1st of each month. A late fee of $25 applies if payment is more than 5 days late.</p>
                    <p><strong style={{color:C.gold}}>PREPAYMENT:</strong> Borrower may pay off the balance early at any time without penalty.</p>
                    <p><strong style={{color:C.gold}}>DEFAULT:</strong> If Borrower fails to make 2 consecutive payments, the full remaining balance becomes due immediately.</p>
                    <p><strong style={{color:C.gold}}>HIPAA & PRIVACY:</strong> All financial information collected in connection with this agreement is subject to our Privacy Policy and HIPAA regulations.</p>
                    <p><strong style={{color:C.gold}}>GOVERNING LAW:</strong> This Agreement shall be governed by the laws of the State of California.</p>
                    <p>By signing below, Borrower acknowledges reading and agreeing to all terms of this Agreement.</p>
                  </div>
                </div>

                {!signed?(
                  <div>
                    <Fld label="Type Your Full Name to Sign">
                      <input value={sigName} onChange={e=>setSigName(e.target.value)} placeholder={form.name} style={inp}/>
                    </Fld>
                    <div style={{marginTop:12,padding:"10px 14px",background:C.goldBg,borderRadius:8,border:`1px solid ${C.borderGold}`,fontSize:11,color:C.gold}}>
                      ✍️ By typing your name above, you are providing a legally binding electronic signature pursuant to the E-SIGN Act.
                    </div>
                    <div style={{marginTop:16,display:"flex",gap:10,justifyContent:"flex-end"}}>
                      <Btn v="ghost" onClick={()=>setCheckStep(4)}>← Back</Btn>
                      <Btn onClick={()=>{if(sigName.toLowerCase().trim()===form.name.toLowerCase().trim()){setSigned(true);}else{alert("Name must match exactly");}}} disabled={!sigName}>Sign & Activate Plan ✓</Btn>
                    </div>
                  </div>
                ):(
                  <div style={{textAlign:"center",padding:24}}>
                    <div style={{fontSize:40,marginBottom:12}}>✅</div>
                    <div style={{fontSize:20,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>Agreement Signed!</div>
                    <div style={{fontSize:13,color:C.muted,marginBottom:20}}>Payment plan is now active. ${selPlan.monthly.toFixed(2)}/month will be charged automatically.</div>
                    <div style={{padding:"14px",background:C.greenL,borderRadius:10,border:"1px solid rgba(46,204,135,0.25)",marginBottom:16,fontSize:12,color:C.green}}>
                      📧 Contract PDF has been sent to client's email · 📱 SMS confirmation sent
                    </div>
                    <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                      <Btn v="sec">Download Contract PDF</Btn>
                      <Btn onClick={()=>{setPlans(ps=>[...ps,{id:Date.now(),client:form.name,cid:Date.now(),amount:selPlan.amount,approved:checkResult.approvedAmount,score:checkResult.score,apr:selPlan.apr,months:selPlan.months,monthly:selPlan.monthly,balance:selPlan.amount,paid:0,status:"active",startDate:new Date().toISOString().split("T")[0],nextDue:new Date(Date.now()+30*86400000).toISOString().split("T")[0],treats:[form.treats||"Treatment"],signed:true}]);resetCheck();setView("dashboard");}}>Go to Dashboard</Btn>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Right Panel */}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Card style={{padding:18,background:`linear-gradient(135deg,rgba(201,168,76,0.1),rgba(201,168,76,0.03))`}}>
              <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:12}}>Why Elyon Pay?</div>
              {[["No third-party fees","Keep 100% of finance charges"],["Soft pull only","No impact on client's credit"],["Your terms","Set your own APR & limits"],["Legal protection","Digital contract via E-SIGN Act"],["Auto payments","Card charged automatically"]].map(([t,s])=>(
                <div key={t} style={{display:"flex",gap:10,marginBottom:10}}>
                  <div style={{width:20,height:20,borderRadius:"50%",background:C.greenL,border:"1px solid rgba(46,204,135,0.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:C.green,flexShrink:0}}>✓</div>
                  <div><div style={{fontSize:12,fontWeight:600,color:C.text}}>{t}</div><div style={{fontSize:11,color:C.muted}}>{s}</div></div>
                </div>
              ))}
            </Card>
            <Card style={{padding:18}}>
              <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:12}}>📊 This Month</div>
              {[["New Plans","3",C.gold],["Payments Collected","$2,847",C.green],["Finance Charges","$284",C.purple],["On-time Rate","96%",C.teal]].map(([l,v,c])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                  <span style={{color:C.muted}}>{l}</span><span style={{fontWeight:700,color:c}}>{v}</span>
                </div>
              ))}
            </Card>
          </div>
        </div>
      )}

      {/* Active Plans */}
      {view==="plans"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {plans.map(p=>{
            const pct=Math.round((p.paid/p.amount)*100);
            return(
              <Card key={p.id} style={{padding:22}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:14}}>
                  <div>
                    <div style={{fontSize:16,fontWeight:700,color:C.text,fontFamily:S,marginBottom:3}}>{p.client}</div>
                    <div style={{fontSize:12,color:C.muted}}>{p.treats.join(", ")} · Score: {p.score} · {p.apr}% APR · {p.months} months</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S}}>${p.amount.toLocaleString()}</div>
                    <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:p.status==="active"?C.greenL:C.blueL,color:p.status==="active"?C.green:C.blue,fontWeight:600}}>{p.status}</span>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:14}}>
                  {[["Monthly",`$${p.monthly.toFixed(2)}`,C.gold],["Paid",`$${p.paid.toLocaleString()}`,C.green],["Balance",`$${p.balance.toLocaleString()}`,p.balance>0?C.orange:C.green],["Next Due",p.nextDue||"Complete",C.blue]].map(([l,v,c])=>(
                    <div key={l} style={{padding:"10px",background:C.surface,borderRadius:8,textAlign:"center",border:`1px solid ${C.border}`}}>
                      <div style={{fontSize:11,color:C.muted,marginBottom:3}}>{l}</div>
                      <div style={{fontSize:13,fontWeight:700,color:c}}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{marginBottom:10}}>
                  <div style={{height:6,background:C.light,borderRadius:3}}><div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${C.goldD},${C.gold})`,borderRadius:3}}/></div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.dim,marginTop:3}}><span>{pct}% paid</span><span>{100-pct}% remaining</span></div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  {p.status==="active"&&<><Btn sz="sm" v="sec">Record Payment</Btn><Btn sz="sm" v="ghost">Modify Terms</Btn><Btn sz="sm" v="ghost">Send Reminder</Btn></>}
                  <Btn sz="sm" v="ghost">View Contract</Btn>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Settings */}
      {view==="settings"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
          <Card style={{padding:22}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:18}}>Financing Settings</div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <Fld label="Max Credit Limit ($)"><input type="number" value={spaSettings.maxLimit} onChange={e=>setSpaSettings({...spaSettings,maxLimit:+e.target.value})} style={inp}/></Fld>
              <Fld label="Default APR (%)"><input type="number" step="0.01" value={spaSettings.defaultAPR} onChange={e=>setSpaSettings({...spaSettings,defaultAPR:+e.target.value})} style={inp}/></Fld>
              <Fld label="Down Payment (%)"><input type="number" value={spaSettings.downPaymentPct} onChange={e=>setSpaSettings({...spaSettings,downPaymentPct:+e.target.value})} style={inp}/></Fld>
              <Fld label="Available Plan Lengths"><div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{[3,6,12,18,24].map(m=><label key={m} style={{display:"flex",gap:6,alignItems:"center",cursor:"pointer",fontSize:13,color:C.text}}><input type="checkbox" checked={spaSettings.plans.includes(m)} onChange={e=>setSpaSettings(s=>({...s,plans:e.target.checked?[...s.plans,m].sort():s.plans.filter(x=>x!==m)}))} style={{accentColor:C.gold}}/>{m} mo</label>)}</div></Fld>
              <Btn>Save Settings</Btn>
            </div>
          </Card>
          <Card style={{padding:22}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>APR by Credit Score</div>
            {[["750+","Excellent",9.99,C.green],["700-749","Good",14.99,C.teal],["650-699","Fair",19.99,C.gold],["600-649","Poor",24.99,C.orange],["<600","Declined","—",C.red]].map(([range,label,apr,c])=>(
              <div key={range} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
                <div><div style={{fontSize:13,fontWeight:600,color:C.text}}>{range}</div><div style={{fontSize:11,color:C.muted}}>{label}</div></div>
                <div style={{fontSize:15,fontWeight:800,color:c,fontFamily:S}}>{apr}{typeof apr==="number"?"%":""}</div>
              </div>
            ))}
            <div style={{marginTop:14,padding:"12px",background:C.goldBg,borderRadius:8,border:`1px solid ${C.borderGold}`,fontSize:11,color:C.gold}}>💡 You can customize these rates in your Elyon admin settings to match your risk tolerance.</div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── CLIENT PORTAL ─────────────────────────────────────────────────────────────
function ClientPortal(){
  const [loggedIn,setLoggedIn]=useState(false);
  const [portalTab,setPortalTab]=useState("home");
  const [loginForm,setLoginForm]=useState({email:"",code:""});
  const [codeSent,setCodeSent]=useState(false);
  const client=PORTAL_CLIENT;

  if(!loggedIn){
    return(
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"70vh"}}>
        <div style={{width:"100%",maxWidth:440}}>
          {/* Logo */}
          <div style={{textAlign:"center",marginBottom:32}}>
            <div style={{fontSize:36,fontWeight:800,color:C.gold,fontFamily:S,letterSpacing:"0.1em",marginBottom:4}}>ELYON</div>
            <div style={{fontSize:12,color:C.muted,letterSpacing:"0.2em"}}>CLIENT PORTAL</div>
          </div>

          <Card style={{padding:32}}>
            {!codeSent?(
              <div>
                <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4}}>Welcome Back</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:20}}>Enter your email to receive a secure login code</div>
                <Fld label="Email Address"><input type="email" value={loginForm.email} onChange={e=>setLoginForm({...loginForm,email:e.target.value})} placeholder="your@email.com" style={inp}/></Fld>
                <div style={{marginTop:16}}>
                  <Btn full onClick={()=>{if(loginForm.email)setCodeSent(true);}}>Send Login Code →</Btn>
                </div>
                <div style={{marginTop:16,textAlign:"center",fontSize:11,color:C.dim}}>No password needed. We'll send a secure 6-digit code to your email.</div>
              </div>
            ):(
              <div>
                <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4}}>Check Your Email</div>
                <div style={{fontSize:12,color:C.muted,marginBottom:20}}>We sent a 6-digit code to <strong style={{color:C.text}}>{loginForm.email}</strong></div>
                <Fld label="6-Digit Code"><input value={loginForm.code} onChange={e=>setLoginForm({...loginForm,code:e.target.value})} placeholder="000000" maxLength={6} style={{...inp,fontSize:24,textAlign:"center",letterSpacing:"0.3em"}}/></Fld>
                <div style={{marginTop:16}}>
                  <Btn full onClick={()=>{if(loginForm.code.length===6||true)setLoggedIn(true);}}>Verify & Sign In →</Btn>
                </div>
                <div style={{marginTop:10,textAlign:"center"}}>
                  <button onClick={()=>setCodeSent(false)} style={{background:"none",border:"none",color:C.muted,fontSize:12,cursor:"pointer",fontFamily:F}}>← Use different email</button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {/* Portal Header */}
      <div style={{padding:"20px 24px",background:`linear-gradient(135deg,rgba(201,168,76,0.12),rgba(201,168,76,0.03))`,border:`1px solid ${C.borderGold}`,borderRadius:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",gap:14,alignItems:"center"}}>
          <div style={{width:52,height:52,borderRadius:12,background:C.goldBg,border:`2px solid ${C.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,color:C.gold}}>{client.name.split(" ").map(n=>n[0]).join("")}</div>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S}}>Welcome back, {client.name.split(" ")[0]}!</div>
            <div style={{fontSize:12,color:C.muted}}>{client.memberPlan} Member · {client.tier} Loyalty Tier</div>
          </div>
        </div>
        <div style={{display:"flex",gap:14,alignItems:"center"}}>
          <div style={{textAlign:"center",padding:"10px 16px",background:C.goldBg,borderRadius:10,border:`1px solid ${C.borderGold}`}}>
            <div style={{fontSize:20,fontWeight:800,color:C.gold,fontFamily:S}}>{client.loyaltyPts}</div>
            <div style={{fontSize:10,color:C.muted}}>Loyalty Points</div>
          </div>
          <div style={{textAlign:"center",padding:"10px 16px",background:C.greenL,borderRadius:10,border:"1px solid rgba(46,204,135,0.25)"}}>
            <div style={{fontSize:20,fontWeight:800,color:C.green,fontFamily:S}}>${client.beautyBank}</div>
            <div style={{fontSize:10,color:C.muted}}>Beauty Bank</div>
          </div>
          <button onClick={()=>setLoggedIn(false)} style={{background:C.light,border:"none",borderRadius:8,padding:"8px 14px",cursor:"pointer",color:C.muted,fontSize:12,fontFamily:F}}>Sign Out</button>
        </div>
      </div>

      {/* Portal Nav */}
      <div style={{display:"flex",gap:4,background:C.surface,padding:4,borderRadius:10,border:`1px solid ${C.border}`,flexWrap:"wrap"}}>
        {[["home","🏠 Home"],["appointments","📅 My Appointments"],["payments","💳 Payments"],["forms","📋 My Forms"],["shop","🛍 Shop"]].map(([v,l])=>(
          <button key={v} onClick={()=>setPortalTab(v)} style={{padding:"7px 16px",fontSize:12,fontWeight:portalTab===v?700:500,color:portalTab===v?"#09090b":C.muted,background:portalTab===v?C.gold:"none",borderRadius:7,border:"none",cursor:"pointer",fontFamily:F,transition:"all 0.15s"}}>{l}</button>
        ))}
      </div>

      {/* Home */}
      {portalTab==="home"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <Card style={{padding:20}}>
            <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>📅 Next Appointment</div>
            {client.appointments.filter(a=>a.status==="confirmed").map(a=>(
              <div key={a.id} style={{padding:"14px",background:C.goldBg,borderRadius:10,border:`1px solid ${C.borderGold}`}}>
                <div style={{fontSize:16,fontWeight:700,color:C.text,fontFamily:S,marginBottom:4}}>{a.treat}</div>
                <div style={{fontSize:13,color:C.muted,marginBottom:2}}>with {a.prov}</div>
                <div style={{fontSize:13,fontWeight:600,color:C.gold}}>{a.date} at {a.time}</div>
                <div style={{display:"flex",gap:8,marginTop:12}}>
                  <Btn sz="sm" v="ghost">Reschedule</Btn>
                  <Btn sz="sm" v="danger">Cancel</Btn>
                </div>
              </div>
            ))}
            <div style={{marginTop:12}}>
              <Btn full v="sec">Book New Appointment →</Btn>
            </div>
          </Card>

          <Card style={{padding:20}}>
            <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>💳 Payment Plan</div>
            <div style={{padding:"14px",background:C.surface,borderRadius:10,border:`1px solid ${C.border}`,marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>Monthly Payment</div>
                <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S}}>${client.paymentPlan.monthly}</div>
              </div>
              <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Due: {client.paymentPlan.nextDue}</div>
              <div style={{height:5,background:C.light,borderRadius:3,marginBottom:6}}>
                <div style={{height:"100%",width:`${((client.paymentPlan.amount-client.paymentPlan.balance)/client.paymentPlan.amount)*100}%`,background:`linear-gradient(90deg,${C.goldD},${C.gold})`,borderRadius:3}}/>
              </div>
              <div style={{fontSize:11,color:C.muted}}>Balance: ${client.paymentPlan.balance} remaining</div>
            </div>
            <Btn full v="primary">Make a Payment</Btn>
          </Card>

          <Card style={{padding:20}}>
            <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>⭐ Loyalty Status</div>
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{fontSize:36,fontWeight:800,color:C.gold,fontFamily:S}}>{client.loyaltyPts}</div>
              <div style={{fontSize:12,color:C.muted}}>points available</div>
              <div style={{display:"inline-block",padding:"4px 16px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:20,fontSize:12,fontWeight:700,color:C.gold,marginTop:8}}>{client.tier} Member</div>
            </div>
            <div style={{fontSize:12,color:C.muted,marginBottom:12}}>Redeem 100 pts = $10 off your next visit</div>
            <Btn full v="sec">Redeem Points</Btn>
          </Card>

          <Card style={{padding:20}}>
            <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>📋 Pending Forms</div>
            {client.forms.map(f=>(
              <div key={f.name} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{fontSize:13,color:C.text}}>{f.name}</div>
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:C.greenL,color:C.green,fontWeight:600}}>✓ Signed {f.date}</span>
              </div>
            ))}
            <div style={{marginTop:12}}>
              <Btn full v="ghost">View All Documents</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* Appointments */}
      {portalTab==="appointments"&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {client.appointments.map(a=>(
            <Card key={a.id} style={{padding:"16px 20px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text,fontFamily:S,marginBottom:3}}>{a.treat}</div>
                  <div style={{fontSize:12,color:C.muted}}>with {a.prov} · {a.date} at {a.time}</div>
                </div>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <div style={{fontSize:15,fontWeight:700,color:C.gold}}>${a.price}</div>
                  <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:a.status==="confirmed"?C.blueL:C.greenL,color:a.status==="confirmed"?C.blue:C.green,fontWeight:600}}>{a.status}</span>
                  {a.status==="confirmed"&&<Btn sz="sm" v="ghost">Reschedule</Btn>}
                </div>
              </div>
            </Card>
          ))}
          <Btn v="primary">Book New Appointment →</Btn>
        </div>
      )}

      {/* Payments */}
      {portalTab==="payments"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <Card style={{padding:22}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:16}}>💳 Make a Payment</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
              {[["Outstanding Balance",`$${client.paymentPlan.balance}`,C.red],["Next Due","Feb 1, 2024",C.gold],["Monthly Amount",`$${client.paymentPlan.monthly}`,C.green],["Card on File","Visa ****4242",C.blue]].map(([l,v,c])=>(
                <div key={l} style={{padding:"14px",background:C.surface,borderRadius:10,border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4}}>{l}</div>
                  <div style={{fontSize:15,fontWeight:700,color:c}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:10}}>
              <Btn full v="primary">Pay ${client.paymentPlan.monthly} Now</Btn>
              <Btn full v="sec">Pay Full Balance</Btn>
            </div>
          </Card>

          <Card style={{padding:22}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>Payment History</div>
            {[{date:"Jan 1, 2024",amount:320.44,status:"paid"},{date:"Dec 1, 2023",amount:320.44,status:"paid"},{date:"Nov 1, 2023",amount:320.44,status:"paid"}].map((p,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{fontSize:13,color:C.text}}>{p.date}</div>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.green}}>${p.amount}</div>
                  <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:C.greenL,color:C.green,fontWeight:600}}>✓ Paid</span>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Forms */}
      {portalTab==="forms"&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {[["Botox Consent Form","Signed Jan 22, 2024","valid"],["HIPAA Authorization","Signed Nov 10, 2023","valid"],["Medical Intake Form","Signed Sep 5, 2023","valid"],["Ultherapy Consent Form","Pending signature ⚠️","pending"]].map(([n,d,s])=>(
            <Card key={n} style={{padding:"16px 20px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:C.text}}>{n}</div>
                  <div style={{fontSize:12,color:s==="pending"?C.gold:C.muted}}>{d}</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  {s==="pending"?<Btn sz="sm" v="primary">Sign Now ✍️</Btn>:<Btn sz="sm" v="ghost">View PDF</Btn>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Shop */}
      {portalTab==="shop"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16}}>
            {RETAIL_PRODUCTS.map(p=>(
              <Card key={p.id} style={{padding:18}}>
                <div style={{fontSize:36,marginBottom:12,textAlign:"center"}}>{p.img}</div>
                <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:3}}>{p.name}</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:3}}>{p.brand} · {p.cat}</div>
                <div style={{fontSize:11,color:C.dim,marginBottom:12}}>{p.desc}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S}}>${p.price}</div>
                  <Btn sz="sm" v="sec">Add to Cart</Btn>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── GIFT CARDS ────────────────────────────────────────────────────────────────
function GiftCards(){
  const [cards,setCards]=useState(GIFT_CARDS);
  const [showF,setShowF]=useState(false);
  const [lookupCode,setLookupCode]=useState("");
  const [lookupResult,setLookupResult]=useState(null);
  const [form,setForm]=useState({recipient:"",from:"",amount:250,email:"",message:""});

  const lookup=()=>{
    const card=cards.find(c=>c.code.toLowerCase()===lookupCode.toLowerCase());
    setLookupResult(card||"notfound");
  };

  const totalActive=cards.filter(c=>c.status==="active").reduce((s,c)=>s+c.balance,0);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14}}>
        <StatCard label="Outstanding Balance" value={`$${totalActive}`} color={C.gold} sub="Across all active cards"/>
        <StatCard label="Active Cards" value={cards.filter(c=>c.status==="active").length} color={C.green} sub={`${cards.length} total`}/>
        <StatCard label="Sold This Month" value="$3,250" color={C.purple} trend={18}/>
        <StatCard label="Redeemed This Month" value="$1,820" color={C.teal}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Gift Cards</div>
            <Btn v="sec" sz="sm" onClick={()=>setShowF(true)}>+ Issue Gift Card</Btn>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {cards.map(card=>(
              <Card key={card.id} style={{padding:"16px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontFamily:"monospace",fontSize:12,color:C.gold,fontWeight:700,marginBottom:4}}>{card.code}</div>
                    <div style={{fontSize:13,fontWeight:600,color:C.text}}>To: {card.recipient}</div>
                    <div style={{fontSize:11,color:C.muted}}>From: {card.from} · Issued: {card.issued}</div>
                    <div style={{fontSize:11,color:C.dim}}>Expires: {card.expiry}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:20,fontWeight:800,color:card.status==="active"?C.gold:C.muted,fontFamily:S}}>${card.balance}</div>
                    <div style={{fontSize:11,color:C.muted}}>of ${card.amount}</div>
                    <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:card.status==="active"?C.greenL:C.blueL,color:card.status==="active"?C.green:C.muted,fontWeight:600}}>{card.status}</span>
                  </div>
                </div>
                {card.status==="active"&&(
                  <div style={{height:4,background:C.light,borderRadius:2}}>
                    <div style={{height:"100%",width:`${(card.balance/card.amount)*100}%`,background:`linear-gradient(90deg,${C.goldD},${C.gold})`,borderRadius:2}}/>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Card style={{padding:22}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>🔍 Redeem / Lookup</div>
            <Fld label="Gift Card Code"><input value={lookupCode} onChange={e=>setLookupCode(e.target.value)} placeholder="ELYON-XXXX-XXXX" style={inp}/></Fld>
            <div style={{marginTop:10}}>
              <Btn full v="sec" onClick={lookup}>Look Up Card</Btn>
            </div>
            {lookupResult&&lookupResult!=="notfound"&&(
              <div style={{marginTop:14,padding:"14px",background:C.greenL,borderRadius:10,border:"1px solid rgba(46,204,135,0.25)"}}>
                <div style={{fontSize:13,fontWeight:700,color:C.green,marginBottom:6}}>✓ Valid Gift Card</div>
                <div style={{fontSize:12,color:C.text}}>Balance: ${lookupResult.balance} of ${lookupResult.amount}</div>
                <div style={{fontSize:11,color:C.muted}}>Recipient: {lookupResult.recipient}</div>
                <div style={{marginTop:10}}><Btn sz="sm" v="primary">Apply to Checkout</Btn></div>
              </div>
            )}
            {lookupResult==="notfound"&&(
              <div style={{marginTop:14,padding:"12px",background:C.redL,borderRadius:10,border:"1px solid rgba(224,85,85,0.25)",fontSize:12,color:C.red}}>❌ Gift card not found or expired</div>
            )}
          </Card>

          <Card style={{padding:22}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>💝 Quick Issue</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
              {[100,250,500].map(amt=>(
                <button key={amt} onClick={()=>setForm({...form,amount:amt})} style={{padding:"12px",background:form.amount===amt?C.goldBg:C.surface,border:`1.5px solid ${form.amount===amt?C.gold:C.border}`,borderRadius:10,cursor:"pointer",fontFamily:F}}>
                  <div style={{fontSize:16,fontWeight:800,color:form.amount===amt?C.gold:C.text,fontFamily:S}}>${amt}</div>
                </button>
              ))}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <Fld label="Recipient Name"><input value={form.recipient} onChange={e=>setForm({...form,recipient:e.target.value})} style={inp}/></Fld>
              <Fld label="Recipient Email"><input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={inp}/></Fld>
            </div>
            <div style={{marginTop:14}}><Btn full onClick={()=>{if(form.recipient){const code=`ELYON-${Math.random().toString(36).slice(2,6).toUpperCase()}-${Math.random().toString(36).slice(2,6).toUpperCase()}`;setCards(cs=>[...cs,{id:Date.now(),code,recipient:form.recipient,from:"Spa",amount:form.amount,balance:form.amount,issued:new Date().toLocaleDateString(),expiry:"2025-12-31",status:"active"}]);setForm({recipient:"",from:"",amount:250,email:"",message:""});}}}>Issue & Send Gift Card ✉️</Btn></div>
          </Card>
        </div>
      </div>

      {showF&&(
        <Modal title="Issue Gift Card" onClose={()=>setShowF(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Fld label="Recipient Name"><input value={form.recipient} onChange={e=>setForm({...form,recipient:e.target.value})} style={inp}/></Fld>
            <Fld label="Recipient Email"><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={inp}/></Fld>
            <Fld label="From (Sender)"><input value={form.from} onChange={e=>setForm({...form,from:e.target.value})} placeholder="Client name or 'Spa'" style={inp}/></Fld>
            <Fld label="Amount ($)"><input type="number" value={form.amount} onChange={e=>setForm({...form,amount:+e.target.value})} style={inp}/></Fld>
            <Fld label="Personal Message (optional)"><textarea value={form.message} onChange={e=>setForm({...form,message:e.target.value})} rows={3} style={{...inp,resize:"none"}} placeholder="Treat yourself to something beautiful..."/></Fld>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20}}>
            <Btn v="ghost" onClick={()=>setShowF(false)}>Cancel</Btn>
            <Btn onClick={()=>{if(form.recipient&&form.email){const code=`ELYON-${Math.random().toString(36).slice(2,6).toUpperCase()}-${Math.random().toString(36).slice(2,6).toUpperCase()}`;setCards(cs=>[...cs,{id:Date.now(),code,recipient:form.recipient,from:form.from||"Spa",amount:form.amount,balance:form.amount,issued:new Date().toLocaleDateString(),expiry:"2025-12-31",status:"active"}]);setShowF(false);}}}> Issue & Send ✉️</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── LOYALTY ───────────────────────────────────────────────────────────────────
function Loyalty(){
  const [clients,setClients]=useState(CLIENTS_LOYALTY);
  const [showRedeem,setShowRedeem]=useState(null);
  const [redeemAmt,setRedeemAmt]=useState(0);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14}}>
        <StatCard label="Total Points Issued" value={clients.reduce((s,c)=>s+c.pts,0).toLocaleString()} color={C.gold} sub="Across all clients" trend={14}/>
        <StatCard label="Points Redeemed" value={clients.reduce((s,c)=>s+c.redeemed,0).toLocaleString()} color={C.green}/>
        <StatCard label="Available Points" value={clients.reduce((s,c)=>s+c.available,0).toLocaleString()} color={C.purple}/>
        <StatCard label="Estimated Value" value={`$${Math.round(clients.reduce((s,c)=>s+c.available,0)/100*10)}`} color={C.teal} sub="100 pts = $10"/>
      </div>

      {/* Tier Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        {LOYALTY_TIERS.map(tier=>(
          <Card key={tier.name} style={{padding:18,borderTop:`2px solid ${tier.color}`}}>
            <div style={{fontSize:15,fontWeight:800,color:tier.color,fontFamily:S,marginBottom:4}}>{tier.name}</div>
            <div style={{fontSize:11,color:C.muted,marginBottom:10}}>${tier.min.toLocaleString()}+ spend</div>
            {tier.perks.map((p,i)=><div key={i} style={{fontSize:11,color:C.muted,display:"flex",gap:6,marginBottom:4}}><span style={{color:tier.color}}>✓</span>{p}</div>)}
            <div style={{marginTop:10,fontSize:12,fontWeight:700,color:tier.color}}>{clients.filter(c=>c.tier===tier.name).length} clients</div>
          </Card>
        ))}
      </div>

      <Card>
        <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`}}>
          <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Client Loyalty Overview</div>
        </div>
        {clients.map(c=>{
          const tier=LOYALTY_TIERS.find(t=>c.tier===t.name);
          return(
            <div key={c.id} style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:14}}>
              <div style={{width:40,height:40,borderRadius:10,background:C.goldBg,border:`1px solid ${C.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:C.gold,flexShrink:0}}>{c.name.split(" ").map(n=>n[0]).join("")}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:600,color:C.text}}>{c.name}</div>
                <div style={{fontSize:11,color:C.muted}}>YTD Spend: ${c.ytdSpend.toLocaleString()}</div>
              </div>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                <div style={{textAlign:"center",padding:"8px 14px",background:C.goldBg,borderRadius:8,border:`1px solid ${C.borderGold}`}}>
                  <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S}}>{c.pts}</div>
                  <div style={{fontSize:10,color:C.muted}}>pts</div>
                </div>
                <div style={{textAlign:"center",padding:"8px 14px",background:C.greenL,borderRadius:8,border:"1px solid rgba(46,204,135,0.25)"}}>
                  <div style={{fontSize:16,fontWeight:800,color:C.green,fontFamily:S}}>{c.available}</div>
                  <div style={{fontSize:10,color:C.muted}}>avail</div>
                </div>
                <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:tier?.color+"20",color:tier?.color,fontWeight:700,border:`1px solid ${tier?.color}30`}}>{c.tier}</span>
                <Btn sz="sm" v="sec" onClick={()=>{setShowRedeem(c);setRedeemAmt(0);}}>Redeem</Btn>
              </div>
            </div>
          );
        })}
      </Card>

      {showRedeem&&(
        <Modal title={`Redeem Points — ${showRedeem.name}`} onClose={()=>setShowRedeem(null)}>
          <div style={{textAlign:"center",marginBottom:20}}>
            <div style={{fontSize:40,fontWeight:800,color:C.gold,fontFamily:S}}>{showRedeem.available}</div>
            <div style={{fontSize:12,color:C.muted}}>available points · 100 pts = $10 off</div>
          </div>
          <Fld label="Points to Redeem">
            <input type="number" value={redeemAmt} max={showRedeem.available} onChange={e=>setRedeemAmt(Math.min(+e.target.value,showRedeem.available))} style={inp}/>
          </Fld>
          {redeemAmt>0&&<div style={{marginTop:12,padding:"12px",background:C.goldBg,borderRadius:8,border:`1px solid ${C.borderGold}`,fontSize:13,color:C.gold,fontWeight:600,textAlign:"center"}}>Discount: ${Math.floor(redeemAmt/100)*10} off next service</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20}}>
            <Btn v="ghost" onClick={()=>setShowRedeem(null)}>Cancel</Btn>
            <Btn onClick={()=>{if(redeemAmt>0){setClients(cs=>cs.map(c=>c.id===showRedeem.id?{...c,available:c.available-redeemAmt,redeemed:c.redeemed+redeemAmt}:c));setShowRedeem(null);}}}> Apply Redemption</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── RETAIL STORE ──────────────────────────────────────────────────────────────
function RetailStore(){
  const [cart,setCart]=useState([]);
  const [filter,setFilter]=useState("All");
  const cats=["All",...new Set(RETAIL_PRODUCTS.map(p=>p.cat))];
  const filtered=filter==="All"?RETAIL_PRODUCTS:RETAIL_PRODUCTS.filter(p=>p.cat===filter);
  const total=cart.reduce((s,i)=>s+(i.price*i.qty),0);

  const addToCart=(p)=>setCart(c=>{const ex=c.find(x=>x.id===p.id);return ex?c.map(x=>x.id===p.id?{...x,qty:x.qty+1}:x):[...c,{...p,qty:1}];});

  return(
    <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:20}}>
      <div>
        <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
          {cats.map(c=>(
            <button key={c} onClick={()=>setFilter(c)} style={{padding:"6px 14px",fontSize:11,fontWeight:filter===c?700:500,color:filter===c?"#09090b":C.muted,background:filter===c?C.gold:C.surface,border:`1px solid ${filter===c?C.gold:C.border}`,borderRadius:20,cursor:"pointer",fontFamily:F,transition:"all 0.15s"}}>{c}</button>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
          {filtered.map(p=>(
            <Card key={p.id} style={{padding:18}}>
              <div style={{fontSize:36,textAlign:"center",marginBottom:12,padding:"16px",background:C.goldBg,borderRadius:10,border:`1px solid ${C.borderGold}`}}>{p.img}</div>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:2}}>{p.name}</div>
              <div style={{fontSize:11,color:C.gold,marginBottom:3}}>{p.brand}</div>
              <div style={{fontSize:11,color:C.muted,marginBottom:10,lineHeight:1.5}}>{p.desc}</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S}}>${p.price}</div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{fontSize:10,color:p.stock>10?C.green:C.orange}}>{p.stock} left</span>
                  <Btn sz="sm" v="sec" onClick={()=>addToCart(p)}>+ Cart</Btn>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Card style={{position:"sticky",top:0}}>
        <div style={{padding:"16px 18px",borderBottom:`1px solid ${C.border}`,background:C.goldBg}}>
          <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>🛒 Cart ({cart.reduce((s,i)=>s+i.qty,0)})</div>
        </div>
        <div style={{padding:18,flex:1}}>
          {cart.length===0&&<div style={{padding:"30px",textAlign:"center",color:C.dim,fontSize:12}}>Cart is empty</div>}
          {cart.map(item=>(
            <div key={item.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:C.text}}>{item.name}</div>
                <div style={{fontSize:11,color:C.muted}}>${item.price} × {item.qty}</div>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <div style={{fontSize:13,fontWeight:700,color:C.gold}}>${item.price*item.qty}</div>
                <button onClick={()=>setCart(c=>c.filter(x=>x.id!==item.id))} style={{background:C.redL,border:"none",borderRadius:6,padding:"3px 6px",cursor:"pointer",color:C.red,fontSize:11}}>✕</button>
              </div>
            </div>
          ))}
          {cart.length>0&&(
            <div style={{marginTop:14}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:15,fontWeight:800,color:C.gold,marginBottom:12,fontFamily:S}}>
                <span>Total</span><span>${total}</span>
              </div>
              <Btn full v="primary">Process Sale</Btn>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ── NAV ───────────────────────────────────────────────────────────────────────
const LOGO_SVG=()=>(
  <svg width={32} height={32} viewBox="0 0 40 40" fill="none">
    <defs><linearGradient id="eg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#e8c96a"/><stop offset="100%" stopColor="#8a6f2e"/></linearGradient></defs>
    <circle cx="20" cy="20" r="19" fill="none" stroke="#c9a84c" strokeWidth="1"/>
    <path d="M20 4L22.5 14.5L33 18L22.5 21.5L20 32L17.5 21.5L7 18L17.5 14.5Z" fill="url(#eg)"/>
    <circle cx="20" cy="20" r="3" fill="#09090b" stroke="#c9a84c" strokeWidth="1"/>
    <circle cx="20" cy="20" r="1.5" fill="url(#eg)"/>
  </svg>
);

const TABS=[
  {id:"pay",label:"Elyon Pay",emoji:"💳"},
  {id:"portal",label:"Client Portal",emoji:"🌐"},
  {id:"gifts",label:"Gift Cards",emoji:"🎁"},
  {id:"loyalty",label:"Loyalty",emoji:"⭐"},
  {id:"retail",label:"Retail Store",emoji:"🛍"},
];

export default function App(){
  const [tab,setTab]=useState("pay");

  return(
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:F,color:C.text}}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&family=Cormorant+Garamond:wght@400;600;700;800&display=swap" rel="stylesheet"/>

      {/* Top Nav */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"0 32px",height:58,display:"flex",alignItems:"center",gap:20,position:"sticky",top:0,zIndex:50}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginRight:20}}>
          <LOGO_SVG/>
          <div style={{fontSize:16,fontWeight:800,color:"#c9a84c",fontFamily:S,letterSpacing:"0.1em"}}>ELYON</div>
          <div style={{fontSize:10,color:C.dim,background:C.goldBg,padding:"2px 8px",borderRadius:10,border:`1px solid rgba(201,168,76,0.2)`,letterSpacing:"0.1em"}}>PART 2</div>
        </div>
        <div style={{display:"flex",gap:4}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"6px 14px",fontSize:12,fontWeight:tab===t.id?700:500,color:tab===t.id?"#09090b":C.muted,background:tab===t.id?"#c9a84c":"transparent",border:`1px solid ${tab===t.id?"#c9a84c":C.border}`,borderRadius:8,cursor:"pointer",fontFamily:F,transition:"all 0.15s",display:"flex",alignItems:"center",gap:6}}>
              <span>{t.emoji}</span><span>{t.label}</span>
            </button>
          ))}
        </div>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,padding:"4px 12px",background:"rgba(46,204,135,0.1)",borderRadius:8,border:"1px solid rgba(46,204,135,0.2)"}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:"#2ecc87",boxShadow:"0 0 8px #2ecc87"}}/>
          <div style={{fontSize:10,color:"#c9a84c",fontWeight:700,letterSpacing:"0.08em"}}>HIPAA SECURE</div>
        </div>
      </div>

      {/* Content */}
      <div style={{padding:"28px 32px"}}>
        {/* Page Title */}
        <div style={{marginBottom:24}}>
          <div style={{fontSize:26,fontWeight:800,color:C.text,fontFamily:S,letterSpacing:"0.03em"}}>{TABS.find(t=>t.id===tab)?.emoji} {TABS.find(t=>t.id===tab)?.label}</div>
          <div style={{fontSize:12,color:C.muted,marginTop:4}}>Elyon Med Spa Platform · Beverly Hills</div>
        </div>

        {tab==="pay"&&<ElyonPay/>}
        {tab==="portal"&&<ClientPortal/>}
        {tab==="gifts"&&<GiftCards/>}
        {tab==="loyalty"&&<Loyalty/>}
        {tab==="retail"&&<RetailStore/>}
      </div>
    </div>
  );
}
